/**
 * FeaturesPage.tsx — ɳSentry features / plugin catalog
 *
 * Purpose:    Lists all 13 ɳSentry plugins with descriptions and install commands
 * Inputs:     sentry-plugins lib data
 * Outputs:    Baseline + expansion plugin grids + install command
 * SOT:        T-P3-E3-W2-S5-T01
 */
import { Link } from 'react-router-dom'
import {
  ALL_SENTRY_PLUGINS,
  BASELINE_PLUGINS,
  EXPANSION_PLUGINS,
  PLUGIN_COUNTS,
} from '../lib/sentry-plugins'

function CategoryIcon({ category }: { category: string }) {
  const icons: Record<string, string> = {
    uptime: '↑', status: '◎', incidents: '⚡', alerts: '🔔', slo: '◉',
    synthetic: '▶', rum: '👤', errors: '✕', cron: '⏱', oncall: '📟',
    crash: '💥', anomaly: '〰', audit: '📋',
  }
  return <span aria-hidden="true">{icons[category] ?? '•'}</span>
}

function PluginCard({ slug, name, description, category, port, tier }: {
  slug: string; name: string; description: string; category: string; port: number; tier: 'bundle' | 'free'
}) {
  return (
    <article className="flex flex-col gap-3 rounded-xl border border-brand-subtle bg-brand-surface/40 p-5 transition hover:border-sky-200 dark:border-sky-800/60" aria-label={name}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 dark:bg-sky-950 text-base text-sky-700 dark:text-sky-400">
            <CategoryIcon category={category} />
          </span>
          <h3 className="font-semibold text-brand-ink">{name}</h3>
        </div>
        {tier === 'free' && (
          <span className="flex-shrink-0 rounded-full border border-emerald-200 dark:border-emerald-700/50 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">FREE</span>
        )}
      </div>
      <p className="text-sm leading-relaxed text-brand-muted">{description}</p>
      <div className="mt-auto pt-1">
        <code className="rounded bg-brand-surface-2 px-2 py-0.5 text-xs font-mono text-brand-muted">nself plugin install {slug}</code>
        <span className="ml-3 text-xs text-gray-700">port {port}</span>
      </div>
    </article>
  )
}

export function FeaturesPage() {
  return (
    <>
      <section className="border-b border-brand-subtle bg-brand-bg px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-200 dark:border-sky-800/50 bg-sky-50 dark:bg-sky-950/50 px-3 py-1 text-sm text-sky-700 dark:text-sky-300">
            {PLUGIN_COUNTS.total} plugins included
          </div>
          <h1 className="mb-4 text-4xl font-bold sm:text-5xl">Everything in ɳSentry</h1>
          <p className="mx-auto max-w-2xl text-lg text-brand-muted">
            {PLUGIN_COUNTS.baseline} baseline plugins form the core observability stack. {PLUGIN_COUNTS.expansion - 1} expansion plugins extend it — plus{' '}
            <span className="font-medium text-emerald-700 dark:text-emerald-400">nself-audit is free in all tiers</span>.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8 space-y-16">
        <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/40 bg-emerald-50 dark:bg-emerald-950/20 px-6 py-4 flex items-start gap-3">
          <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
          </svg>
          <p className="text-sm text-emerald-700 dark:text-emerald-200">
            <span className="font-semibold">Infrastructure monitoring is always free.</span>{' '}
            Prometheus, Grafana, Loki, and the full monitoring stack ship with every ɳSelf instance at no cost.
            ɳSentry adds <em>product-layer</em> observability — status pages your customers see, incident workflows, SLOs, and RUM.
          </p>
        </div>

        <section aria-labelledby="baseline-heading">
          <div className="mb-6">
            <h2 id="baseline-heading" className="text-2xl font-bold">
              Baseline plugins <span className="ml-2 text-lg font-normal text-brand-muted">({PLUGIN_COUNTS.baseline})</span>
            </h2>
            <p className="mt-1 text-sm text-brand-muted">Core observability. All included in the ɳSentry Bundle ($0.99/mo).</p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {BASELINE_PLUGINS.map((plugin) => <PluginCard key={plugin.slug} {...plugin} />)}
          </div>
        </section>

        <section aria-labelledby="expansion-heading">
          <div className="mb-6">
            <h2 id="expansion-heading" className="text-2xl font-bold">
              Expansion plugins <span className="ml-2 text-lg font-normal text-brand-muted">({PLUGIN_COUNTS.expansion})</span>
            </h2>
            <p className="mt-1 text-sm text-brand-muted">
              Extend ɳSentry with advanced capabilities. All included in the ɳSentry Bundle.{' '}
              <span className="text-emerald-700 dark:text-emerald-400">nself-audit is FREE in every tier.</span>
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {EXPANSION_PLUGINS.map((plugin) => <PluginCard key={plugin.slug} {...plugin} />)}
          </div>
        </section>

        <section className="rounded-xl border border-brand-subtle bg-brand-surface/30 px-6 py-8">
          <h2 className="mb-4 text-xl font-bold">Install ɳSentry</h2>
          <p className="mb-4 text-sm text-brand-muted">One command installs all {PLUGIN_COUNTS.total} plugins. Requires a valid ɳSentry Bundle license key.</p>
          <pre className="overflow-x-auto rounded-lg bg-brand-bg p-4 text-sm text-sky-700 dark:text-sky-300">
            <code>{`nself license set nself_pro_xxxx...
nself plugin install ${ALL_SENTRY_PLUGINS.map((p) => p.slug).join(' ')}
nself build && nself start`}</code>
          </pre>
          <p className="mt-4 text-xs text-brand-muted">
            <code className="rounded bg-brand-surface-2 px-1.5 py-0.5 text-brand-muted">nself-audit</code> installs without a license key — it is free in all tiers.
          </p>
        </section>

        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:justify-center">
          <Link to="/pricing" className="rounded-lg bg-sky-500 px-8 py-3 font-semibold text-white transition hover:bg-sky-400">
            Get ɳSentry — $0.99/mo
          </Link>
          <Link to="/demo" className="rounded-lg border border-brand-card px-8 py-3 font-semibold text-brand-secondary transition hover:border-sky-500 hover:text-sky-700 dark:text-sky-400">
            View live demo
          </Link>
        </div>
      </div>
    </>
  )
}
